import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  ChevronLeft,
  Image,
  MapPin,
  Package,
  RefreshCw,
  Send,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { socket } from './socket';
import ChatViewHelpers from './ChatViewHelpers';
import MessageBubble from './MessageBubble';
import MediaModal from './MediaModal';
import { resolveMediaType } from './chatUtils';

const API_URL = process.env.REACT_APP_API_URL;

function field(label, value) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-sm text-slate-900 break-words">{value || '-'}</div>
    </div>
  );
}

function ChatView({ chatId, setActiveComponent, onBackClick, userId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryPointID, setDeliveryPointID] = useState(null);
  const [aiState, setAiState] = useState(null);
  const [aiDraftText, setAiDraftText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingDraft, setIsSendingDraft] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isSendingTemplate, setIsSendingTemplate] = useState(null);
  const endRef = useRef(null);

  const normalizedUserId = userId == null ? null : Number(String(userId).trim());
  const isAdmin = normalizedUserId === 2;

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);

  async function fetchAIState(currentId = chatId) {
    if (!currentId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/chats/${currentId}/ai`);
      if (!res.ok) throw new Error(`ai state ${res.status}`);
      const data = await res.json();
      setAIState(data);
      setAiDraftText(data.pending_draft?.draft_text || '');
      const dpId = data.context?.delivery?.id || null;
      setDeliveryPointID(dpId);
    } catch (error) {
      console.error('AI state error:', error);
      setAIState(null);
      setAiDraftText('');
    }
  }

  async function fetchTemplates(currentId = chatId) {
    if (!currentId) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/chats/${currentId}/templates`);
      if (!res.ok) throw new Error(`templates ${res.status}`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Template list error:', error);
      setTemplates([]);
    }
  }

  function setAIState(data) {
    setAiState(data);
    if (data?.pending_draft) setAiDraftText(data.pending_draft.draft_text || '');
  }

  useEffect(() => {
    if (chatId == null) return;
    const currentId = Number(chatId);
    const room = `chat_${currentId}`;

    fetch(`${API_URL}/api/admin/chats/${currentId}/messages`)
      .then(r => r.json())
      .then(data => {
        setMessages(Array.isArray(data) ? data : []);
        const candidate = Array.isArray(data) && data.length
          ? data[0].phone_number ?? data[0].from ?? data[0].sender_phone ?? null
          : null;
        if (candidate) setPhoneNumber(candidate);
      })
      .catch(err => console.error('Fetch messages error:', err));

    fetch(`${API_URL}/api/admin/chats/${currentId}/session`)
      .then(r => r.json())
      .then(data => setSessionOpen(!!data.session_open))
      .catch(err => console.error('Fetch session error:', err));

    fetchAIState(currentId);
    fetchTemplates(currentId);

    const doJoin = () => socket.emit('admin_join_chat', room);
    if (socket.connected) doJoin();
    else socket.once('connect', doJoin);

    const onNewMessage = (msg) => {
      if (Number(msg?.chat_id) !== currentId) return;
      setMessages(prev => [...prev, msg]);
      setPhoneNumber(prev => prev || msg.phone_number || msg.from || msg.sender_phone || null);
    };

    const onAIState = (payload) => {
      if (Number(payload?.chat_id) !== currentId) return;
      setAIState(payload);
    };

    socket.on('new_message', onNewMessage);
    socket.on('logistics_ai_state_updated', onAIState);

    return () => {
      socket.off('new_message', onNewMessage);
      socket.off('logistics_ai_state_updated', onAIState);
      socket.emit('admin_leave_chat', room);
    };
  }, [chatId]);

  async function send() {
    if (!draft.trim()) return;
    if (!sessionOpen) {
      alert('La sesión de WhatsApp está cerrada. Usa una plantilla aprobada para reabrir la conversación.');
      return;
    }

    const content = draft;
    const res = await fetch(`${API_URL}/api/admin/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.message || data.error || 'No se pudo enviar el mensaje.');
      return;
    }
    setDraft('');
  }

  async function generateDraft() {
    setIsGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/chats/${chatId}/ai-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo generar borrador');
      await fetchAIState();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setIsGenerating(false);
    }
  }

  async function acceptDraft() {
    const pending = aiState?.pending_draft;
    if (!pending || !aiDraftText.trim()) return;
    if (!sessionOpen) {
      alert('La sesión de WhatsApp está cerrada. No se puede enviar un borrador como mensaje libre.');
      return;
    }
    setIsSendingDraft(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/chats/${chatId}/ai-drafts/${pending.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: aiDraftText.trim(), user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo enviar el borrador');
      await fetchAIState();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setIsSendingDraft(false);
    }
  }

  async function dismissDraft() {
    const pending = aiState?.pending_draft;
    if (!pending) return;
    await fetch(`${API_URL}/api/admin/chats/${chatId}/ai-drafts/${pending.id}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    await fetchAIState();
  }

  async function updateMode(mode) {
    const res = await fetch(`${API_URL}/api/admin/chats/${chatId}/ai-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    });
    if (res.ok) fetchAIState();
  }

  async function sendTemplate(templateId = 'saludo') {
    setIsSendingTemplate(templateId);
    try {
      const res = await fetch(`${API_URL}/api/admin/chats/${chatId}/send_template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: templateId }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error?.message || data.error || 'No se pudo enviar la plantilla.');
      }
      await Promise.all([
        fetch(`${API_URL}/api/admin/chats/${chatId}/session`)
          .then(r => r.json())
          .then(value => setSessionOpen(!!value.session_open)),
        fetchAIState(),
        fetchTemplates(),
      ]);
      alert('Plantilla enviada correctamente.');
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setIsSendingTemplate(null);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const settings = aiState?.settings || {};
  const pendingDraft = aiState?.pending_draft;
  const delivery = aiState?.context?.delivery || {};
  const visibleTemplates = templates.length ? templates : [{
    id: 'saludo',
    label: 'Confirmacion de entrega',
    description: 'Saludo inicial',
    available: true,
  }];

  return (
    <div className="flex h-full min-w-0 bg-slate-100">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="h-16 flex items-center gap-3 px-4 bg-white border-b border-slate-200">
          <button
            onClick={onBackClick || (() => setActiveComponent?.('OptionsAdmin'))}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-700"
            title="Volver"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-950 truncate">{phoneNumber || delivery.recipient_phone || 'Chat'}</div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className={`w-2 h-2 rounded-full ${sessionOpen ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              {sessionOpen ? 'Sesion 24h abierta' : 'Sesion cerrada'}
              {settings.last_sentiment && <span>· {settings.last_sentiment}</span>}
              {settings.last_intent && <span>· {settings.last_intent}</span>}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-1 rounded-lg bg-slate-100 p-1">
            {['off', 'draft', 'auto'].map(mode => (
              <button
                key={mode}
                onClick={() => updateMode(mode)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize ${
                  (settings.mode || 'draft') === mode ? 'bg-white text-[#B1336E] shadow-sm' : 'text-slate-600'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          {isAdmin && deliveryPointID && <ChatViewHelpers deliveryPointID={deliveryPointID} />}
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-100">
          {messages.map(m => (
            <MessageBubble key={m.id} message={m} mediaType={resolveMediaType(m)} />
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-200 bg-white p-4">
          {pendingDraft && (
            <div className="mb-3 rounded-xl border border-purple-200 bg-purple-50 p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
                  <Sparkles className="w-4 h-4" />
                  AI draft
                  <span className="text-xs font-normal text-purple-700">
                    {pendingDraft.intent} · {Math.round((pendingDraft.confidence || 0) * 100)}%
                  </span>
                </div>
                {pendingDraft.needs_human && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
                    <AlertTriangle className="w-3 h-3" /> revisar
                  </span>
                )}
              </div>
              {pendingDraft.reason && (
                <p className="text-xs text-purple-700 mb-2">{pendingDraft.reason}</p>
              )}
              <textarea
                value={aiDraftText}
                onChange={e => setAiDraftText(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-purple-400"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={acceptDraft}
                  disabled={isSendingDraft || !sessionOpen}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#B1336E] text-white text-sm font-semibold disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  {isSendingDraft ? 'Enviando...' : 'Enviar'}
                </button>
                <button
                  onClick={() => setDraft(aiDraftText)}
                  className="px-3 py-2 rounded-lg bg-white border border-purple-200 text-purple-800 text-sm font-semibold"
                >
                  Usar en editor
                </button>
                <button
                  onClick={dismissDraft}
                  className="px-3 py-2 rounded-lg bg-transparent text-slate-500 text-sm font-semibold"
                >
                  Descartar
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMediaModal(true)}
              disabled={!sessionOpen}
              className="p-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
              title={sessionOpen ? 'Enviar imagen' : 'La sesión está cerrada'}
            >
              <Image className="w-5 h-5" />
            </button>
            <button
              onClick={generateDraft}
              disabled={isGenerating}
              className="p-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50"
              title="Generar borrador AI"
            >
              {isGenerating ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Bot className="w-5 h-5" />}
            </button>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={!sessionOpen}
              placeholder={sessionOpen ? 'Escribe un mensaje...' : 'Sesión cerrada: usa una plantilla aprobada'}
              rows={1}
              className="flex-1 resize-none bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-4 py-3 outline-none focus:border-[#B1336E] disabled:text-slate-400"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || !sessionOpen}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#B1336E] text-white rounded-lg font-semibold hover:bg-[#932b5d] disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              Enviar
            </button>
          </div>
          {!sessionOpen && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="text-sm text-amber-900">
                La ventana de 24h esta cerrada. WhatsApp no permite mensajes libres; envia una plantilla aprobada para reabrir la conversacion.
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Plantillas aprobadas</div>
                  <div className="text-xs text-slate-500">Se pueden enviar aunque la ventana de 24h este abierta.</div>
                </div>
                <button
                  onClick={() => fetchTemplates()}
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-semibold"
                >
                  Actualizar
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {visibleTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => sendTemplate(template.id)}
                    disabled={!template.available || isSendingTemplate === template.id}
                    title={template.disabled_reason || template.description}
                    className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#B1336E] disabled:opacity-50 disabled:hover:border-slate-200"
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {isSendingTemplate === template.id ? 'Enviando...' : template.label}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {template.disabled_reason || template.description}
                    </div>
                    {template.selected_meta_template && (
                      <div className="mt-2 text-[11px] text-[#B1336E] font-semibold">
                        {template.selected_meta_template}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <aside className="hidden xl:block w-80 bg-white border-l border-slate-200 overflow-y-auto">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-950 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#B1336E]" />
            Delivery context
          </h3>
          <p className="text-xs text-slate-500 mt-1">Datos usados por el borrador AI</p>
        </div>
        <div className="p-4 space-y-3">
          {!delivery.found ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No se encontro una entrega asociada a este telefono.
            </div>
          ) : (
            <>
              {field('ID', delivery.id)}
              {field('Cliente', delivery.recipient_name)}
              {field('Estado', delivery.status)}
              {field('Fecha / horario', [delivery.delivery_date, delivery.slot_label].filter(Boolean).join(' · '))}
              {field('Distrito', delivery.district)}
              {field('Direccion', delivery.address)}
              {field('Producto', delivery.product)}
              {field('Monto', delivery.charge != null ? `S/ ${Number(delivery.charge).toFixed(2)}` : null)}
              {field('Repartidor', delivery.driver_name)}
              {delivery.tracking_url && (
                <a
                  href={delivery.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-semibold"
                >
                  <MapPin className="w-4 h-4" />
                  Ver seguimiento
                </a>
              )}
            </>
          )}

          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
              <UserRound className="w-4 h-4 text-[#B1336E]" />
              AI triage
            </div>
            {field('Modo', settings.mode || 'draft')}
            {field('Sentiment', settings.last_sentiment || 'neutral')}
            {field('Intent', settings.last_intent)}
            {settings.is_handoff_required && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                {settings.handoff_reason || 'Requiere atencion humana'}
              </div>
            )}
          </div>
        </div>
      </aside>

      {showMediaModal && <MediaModal chatId={chatId} onClose={() => setShowMediaModal(false)} />}
    </div>
  );
}

export default ChatView;
